// src/pages/NotificationsPage.jsx
import React, {
  useMemo,
  useState,
  useCallback,
  useRef,
  useEffect,
} from "react";
import {
  Squares2X2Icon,
  HeartIcon,
  ChatBubbleLeftIcon,
  BellAlertIcon,
} from "@heroicons/react/24/outline";
import { useNavigate } from "react-router-dom";
import FilterBar from "../ui/Filterbar";
import { NotificationCard } from "../components/notifications/NotificationCard";
import { cx } from "../utils/cx";
import NoteModal from "../components/notes/NoteModal";
import Spinner from "../ui/Spinner";
import { useNotifications } from "../hooks/useNotifications";
import { useLocation } from "../contexts/LocationContext";
import { useSettings } from "../contexts/SettingsContext";
import { useInfiniteScroll } from "../hooks/useInfiniteScroll";
import { logError } from "../utils/logger";
import ErrorBanner from "../ui/ErrorBanner";

export default function NotificationsPage() {
  const [openNote, setOpenNote] = useState(false);
  const [selectedNote, setSelectedNote] = useState(null);
  const navigate = useNavigate();

  // Get user location for distance calculations
  const { userLocation, requestLocation } = useLocation();

  // Format coordinates to match what NotificationCard expects (lat/lng format)
  const userCoords = userLocation
    ? {
        lat: userLocation.latitude,
        lng: userLocation.longitude,
      }
    : null;

  // Request location when component mounts
  useEffect(() => {
    requestLocation();
  }, []);

  // Use the notifications hook (replaces all hardcoded data)
  const {
    newNotifications,
    olderNotifications,
    stats,
    statsLoading,
    loading,
    error,
    filter,
    setFilter,
    hasMore,
    loadMore,
    loadingMore,
    markAsRead,
    markAllAsRead,
    updateLastSeen,
  } = useNotifications("all", 20);

  // Set up infinite scroll
  const sentinelRef = useInfiniteScroll(
    loadMore, // Callback to load more
    hasMore, // Whether there are more items
    loadingMore, // Whether currently loading
    0.1, // Trigger when 10% visible (loads slightly before reaching bottom)
  );

  // Track session seen time
  const sessionSeenAtRef = useRef(Date.now());

  // Update last seen when leaving page
  useEffect(() => {
    return () => {
      updateLastSeen(sessionSeenAtRef.current);
    };
  }, [updateLastSeen]);

  // Note modal handlers
  const handleNoteModalClose = () => {
    setOpenNote(false);
    setSelectedNote(null);
  };

  const onOpenNote = useCallback(
    (notificationId) => {
      // Find the notification to get its details (search in all, not just filtered)
      const notification = [...newNotifications, ...olderNotifications].find(
        (n) => n.id === notificationId,
      );

      if (!notification) {
        logError("Notification not found:", notificationId);
        return;
      }

      const { noteId, type, commentId } = notification;

      // Legacy bulk nearby notifications have no noteId — navigate to home and fit bounds
      if (!noteId) {
        navigate("/home", { state: { fitNewNearbyNotes: true } });
        return;
      }

      // Navigate based on notification type
      if ((type === "comment_like" || type === "comment") && commentId) {
        // For comment likes and comments: open note with comments section and highlight the comment
        navigate(
          `/n/${noteId}?openComments=true&highlightComment=${commentId}`,
          { state: { from: "/notifications" } },
        );
      } else {
        // For all other types (like, nearby_note, comment_like without commentId): open the note
        navigate(`/n/${noteId}`, { state: { from: "/notifications" } });
      }
    },
    [newNotifications, olderNotifications, navigate],
  );

  // Mark as read handlers
  const onMarkRead = useCallback(
    async (id) => {
      try {
        await markAsRead(id);
      } catch (err) {
        logError("Failed to mark as read:", err);
      }
    },
    [markAsRead],
  );

  const markAllReadVisible = useCallback(async () => {
    try {
      await markAllAsRead();
    } catch (err) {
      logError("Failed to mark all as read:", err);
    }
  }, [markAllAsRead]);

  // Get notification preferences from settings
  const { settings } = useSettings();
  const notificationPrefs = settings?.notifications || {
    likes: true,
    comments: true,
    system: true,
  };

  // Filter stats based on user preferences
  const filteredStats = useMemo(() => {
    const filtered = {
      all: 0,
      likes: notificationPrefs.likes ? stats.likes : 0,
      comments: notificationPrefs.comments ? stats.comments : 0,
      system: notificationPrefs.system ? stats.system : 0,
    };
    // Calculate total only from enabled types
    filtered.all = filtered.likes + filtered.comments + filtered.system;
    return filtered;
  }, [stats, notificationPrefs]);

  // Filter configuration
  const notificationFilters = [
    {
      key: "all",
      label: "All",
      icon: <Squares2X2Icon className="h-4 w-4" />,
      count: filteredStats.all,
      loading: statsLoading,
    },
    {
      key: "likes",
      label: "Likes",
      icon: <HeartIcon className="h-4 w-4" />,
      count: filteredStats.likes,
      loading: statsLoading,
    },
    {
      key: "comments",
      label: "Comments",
      icon: <ChatBubbleLeftIcon className="h-4 w-4" />,
      count: filteredStats.comments,
      loading: statsLoading,
    },
    {
      key: "system",
      label: "System",
      icon: <BellAlertIcon className="h-4 w-4" />,
      count: filteredStats.system,
      loading: statsLoading,
    },
  ];

  // Map notification types to preference keys
  const typeToPreference = {
    like: "likes",
    comment_like: "likes",
    comment: "comments",
    nearby_note: "system",
  };

  const filteredNewNotifications = useMemo(() => {
    return newNotifications.filter((n) => {
      const prefKey = typeToPreference[n.type];
      return prefKey && notificationPrefs[prefKey];
    });
  }, [newNotifications, notificationPrefs]);

  const filteredOlderNotifications = useMemo(() => {
    return olderNotifications.filter((n) => {
      const prefKey = typeToPreference[n.type];
      return prefKey && notificationPrefs[prefKey];
    });
  }, [olderNotifications, notificationPrefs]);

  // Check if any notifications are unread
  const hasUnread = [
    ...filteredNewNotifications,
    ...filteredOlderNotifications,
  ].some((n) => !n.read);

  return (
    <>
      <FilterBar
        items={notificationFilters}
        value={filter}
        onChange={setFilter}
        ariaLabel="Filters"
      />

      <div className="flex-1 overflow-y-auto pretty-scroll bg-surface">
        {loading ? (
          <div className="flex items-center justify-center h-full">
            <Spinner size={32} className="text-text-muted" />
          </div>
        ) : error ? (
          <div className="flex justify-center py-10 px-6">
            <ErrorBanner
              message={error}
              onRetry={() => window.location.reload()}
              dismissible={false}
            />
          </div>
        ) : (
          <div className="max-w-3xl mx-auto px-6 py-4">
            {/* Mark all as read button */}
            <div className="flex items-center justify-end mb-2">
              <button
                className={cx(
                  "px-3 py-1.5 text-sm rounded-full border",
                  hasUnread
                    ? "bg-primary text-text-inverse border-primary hover:opacity-90"
                    : "bg-surface-sunken text-text-muted border cursor-not-allowed",
                )}
                disabled={!hasUnread}
                onClick={markAllReadVisible}
              >
                Mark all as read
              </button>
            </div>

            {/* New notifications */}
            {filteredNewNotifications.length > 0 && (
              <section className="mb-6">
                <div className="text-xs font-medium text-text-secondary mb-3">
                  New
                </div>
                <ul className="space-y-3">
                  {filteredNewNotifications.map((n) => (
                    <NotificationCard
                      key={n.id}
                      notification={n}
                      onOpenNote={onOpenNote}
                      onMarkRead={onMarkRead}
                      userCoords={userCoords}
                    />
                  ))}
                </ul>
              </section>
            )}

            {/* Divider */}
            {filteredNewNotifications.length > 0 &&
              filteredOlderNotifications.length > 0 && (
                <div className="relative my-4">
                  <div className="h-px bg-surface-muted" />
                </div>
              )}

            {/* Older notifications */}
            <section>
              {filteredOlderNotifications.length > 0 ? (
                <>
                  <ul className="space-y-3">
                    {filteredOlderNotifications.map((n) => (
                      <NotificationCard
                        key={n.id}
                        notification={n}
                        onOpenNote={onOpenNote}
                        onMarkRead={onMarkRead}
                        userCoords={userCoords}
                      />
                    ))}
                  </ul>

                  {/* Sentinel element for infinite scroll */}
                  <div
                    ref={sentinelRef}
                    className="h-20 flex items-center justify-center"
                  >
                    {loadingMore && (
                      <div className="flex items-center gap-2 text-text-muted">
                        <Spinner size={20} />
                        <span className="text-sm">
                          Loading more notifications...
                        </span>
                      </div>
                    )}
                    {!loadingMore &&
                      !hasMore &&
                      filteredOlderNotifications.length > 0 && (
                        <p className="text-sm text-text-muted">
                          You've reached the end
                        </p>
                      )}
                  </div>
                </>
              ) : filteredNewNotifications.length === 0 ? (
                <div className="flex items-center justify-center py-20">
                  <p className="text-sm text-text-muted">No notifications</p>
                </div>
              ) : null}
            </section>
          </div>
        )}
      </div>

      <NoteModal
        open={openNote}
        note={selectedNote}
        onClose={handleNoteModalClose}
      />
    </>
  );
}
